<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Record ANC Visit</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Pregnant Woman -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Pregnant Woman</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchPregnantWomen" type="text"
                         class="form-control" placeholder="Search by name or CBHI number...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Categorized Lists -->
              <!-- Overdue -->
              <div v-if="pregnantWomen.overdue && pregnantWomen.overdue.length > 0" class="mb-4">
                <h6 class="text-danger mb-2">
                  <i class="bx bx-error-circle"></i> Overdue for Delivery
                </h6>
                <div class="list-group mb-3">
                  <div v-for="woman in pregnantWomen.overdue" :key="woman.pregnancy_id"
                       class="list-group-item list-group-item-action border-danger"
                       :class="{'active': selectedPregnancy?.pregnancy_id === woman.pregnancy_id}"
                       @click="selectPregnancy(woman)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ woman.first_name }} {{ woman.surname }}</h6>
                        <p class="mb-0 text-muted">
                          EDD: {{ formatDate(woman.edd_date) }} •
                          {{ woman.household_number }}
                        </p>
                        <small class="text-danger">
                          {{ Math.abs(woman.days_to_delivery) }} days overdue •
                          ANC Visits: {{ woman.anc_visit_count }}
                        </small>
                      </div>
                      <div>
                        <span class="badge bg-danger">OVERDUE</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Due This Week -->
              <div v-if="pregnantWomen.due_this_week && pregnantWomen.due_this_week.length > 0" class="mb-4">
                <h6 class="text-warning mb-2">
                  <i class="bx bx-time-five"></i> Due This Week
                </h6>
                <div class="list-group mb-3">
                  <div v-for="woman in pregnantWomen.due_this_week" :key="woman.pregnancy_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedPregnancy?.pregnancy_id === woman.pregnancy_id}"
                       @click="selectPregnancy(woman)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ woman.first_name }} {{ woman.surname }}</h6>
                        <p class="mb-0 text-muted">
                          EDD: {{ formatDate(woman.edd_date) }} •
                          Week {{ woman.current_gestational_weeks }}
                        </p>
                        <small>
                          Last ANC: {{ woman.last_anc_date ? formatDate(woman.last_anc_date) : 'None' }}
                        </small>
                      </div>
                      <div>
                        <span class="badge bg-warning">{{ woman.days_to_delivery }} days</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- High Risk -->
              <div v-if="pregnantWomen.high_risk && pregnantWomen.high_risk.length > 0" class="mb-4">
                <h6 class="text-danger mb-2">
                  <i class="bx bx-shield-x"></i> High Risk Pregnancies
                </h6>
                <div class="list-group mb-3">
                  <div v-for="woman in pregnantWomen.high_risk" :key="woman.pregnancy_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedPregnancy?.pregnancy_id === woman.pregnancy_id}"
                       @click="selectPregnancy(woman)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ woman.first_name }} {{ woman.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Week {{ woman.current_gestational_weeks }} •
                          G{{ woman.gravida }}P{{ woman.para }}
                        </p>
                        <small class="text-danger">
                          <i class="bx bx-error"></i> {{ woman.risk_factors }}
                        </small>
                      </div>
                      <div>
                        <span class="badge bg-danger">HIGH RISK</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Regular Follow-up -->
              <div v-if="pregnantWomen.regular_followup && pregnantWomen.regular_followup.length > 0" class="mb-4">
                <h6 class="text-muted mb-2">Regular Follow-up</h6>
                <div class="list-group">
                  <div v-for="woman in pregnantWomen.regular_followup" :key="woman.pregnancy_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedPregnancy?.pregnancy_id === woman.pregnancy_id}"
                       @click="selectPregnancy(woman)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ woman.first_name }} {{ woman.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Week {{ woman.current_gestational_weeks }} •
                          ANC Visits: {{ woman.anc_visit_count }}
                        </p>
                      </div>
                      <div>
                        <span v-if="woman.cbhi_number" class="badge bg-success">CBHI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-if="noPregnantWomen" class="text-center py-4">
                <i class="bx bx-female text-muted" style="font-size: 48px;"></i>
                <p class="mt-3 text-muted">No pregnant women found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: ANC Visit Details -->
          <div v-else-if="step === 2">
            <!-- Patient Info -->
            <div class="alert alert-info mb-4">
              <div class="row">
                <div class="col-md-8">
                  <h6 class="alert-heading">{{ selectedPregnancy.first_name }} {{ selectedPregnancy.surname }}</h6>
                  <div class="row">
                    <div class="col-md-6">
                      <small>
                        <strong>Age:</strong> {{ calculateAge(selectedPregnancy.date_of_birth) }} years<br>
                        <strong>CBHI:</strong> {{ selectedPregnancy.cbhi_number || 'Not enrolled' }}<br>
                        <strong>Phone:</strong> {{ selectedPregnancy.phone_number || 'No phone' }}
                      </small>
                    </div>
                    <div class="col-md-6">
                      <small>
                        <strong>G{{ selectedPregnancy.gravida }}P{{ selectedPregnancy.para }}</strong><br>
                        <strong>LMP:</strong> {{ formatDate(selectedPregnancy.lmp_date) }}<br>
                        <strong>EDD:</strong> {{ formatDate(selectedPregnancy.edd_date) }}
                      </small>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 text-end">
                  <h5 class="mb-1">Week {{ selectedPregnancy.current_gestational_weeks }}</h5>
                  <small class="text-muted">ANC Visit #{{ (selectedPregnancy.anc_visit_count || 0) + 1 }}</small>
                  <div v-if="selectedPregnancy.high_risk" class="mt-2">
                    <span class="badge bg-danger">HIGH RISK PREGNANCY</span>
                  </div>
                </div>
              </div>
            </div>

            <form @submit.prevent="nextStep">
              <!-- Vital Signs -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Vital Signs & Measurements</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-3 mb-3">
                      <label class="form-label">Weight (kg)</label>
                      <input v-model.number="ancForm.weight" type="number" step="0.1"
                             class="form-control" placeholder="65.5">
                    </div>
                    <div class="col-md-3 mb-3">
                      <label class="form-label">Height (cm)</label>
                      <input v-model.number="ancForm.height" type="number"
                             class="form-control" placeholder="165">
                    </div>
                    <div class="col-md-3 mb-3">
                      <label class="form-label">Blood Pressure</label>
                      <div class="input-group">
                        <input v-model.number="ancForm.bloodPressureSystolic" type="number"
                               class="form-control" placeholder="120">
                        <span class="input-group-text">/</span>
                        <input v-model.number="ancForm.bloodPressureDiastolic" type="number"
                               class="form-control" placeholder="80">
                      </div>
                    </div>
                    <div class="col-md-3 mb-3">
                      <label class="form-label">Temperature (°C)</label>
                      <input v-model.number="ancForm.temperature" type="number" step="0.1"
                             class="form-control" placeholder="36.5">
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Fundal Height (cm)</label>
                      <input v-model.number="ancForm.fundalHeight" type="number"
                             class="form-control" placeholder="24">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Fetal Heart Rate (bpm)</label>
                      <input v-model.number="ancForm.fetalHeartRate" type="number"
                             class="form-control" placeholder="140">
                    </div>
                    <div class="col-md-4 mb-3">
                      <label class="form-label">Fetal Position</label>
                      <select v-model="ancForm.fetalPosition" class="form-select">
                        <option value="">Select position</option>
                        <option value="cephalic">Cephalic (Head down)</option>
                        <option value="breech">Breech</option>
                        <option value="transverse">Transverse</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Clinical Assessment -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Clinical Assessment</h6>
                </div>
                <div class="card-body">
                  <!-- Danger Signs -->
                  <div class="mb-3">
                    <label class="form-label">Danger Signs</label>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="vaginal_bleeding"
                                 v-model="ancForm.dangerSigns" id="vaginalBleeding">
                          <label class="form-check-label" for="vaginalBleeding">
                            Vaginal bleeding
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="severe_headache"
                                 v-model="ancForm.dangerSigns" id="severeHeadache">
                          <label class="form-check-label" for="severeHeadache">
                            Severe headache
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="blurred_vision"
                                 v-model="ancForm.dangerSigns" id="blurredVision">
                          <label class="form-check-label" for="blurredVision">
                            Blurred vision
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="convulsions"
                                 v-model="ancForm.dangerSigns" id="convulsions">
                          <label class="form-check-label" for="convulsions">
                            Convulsions
                          </label>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="severe_abdominal_pain"
                                 v-model="ancForm.dangerSigns" id="abdominalPain">
                          <label class="form-check-label" for="abdominalPain">
                            Severe abdominal pain
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="reduced_fetal_movement"
                                 v-model="ancForm.dangerSigns" id="reducedMovement">
                          <label class="form-check-label" for="reducedMovement">
                            Reduced fetal movement
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="fever"
                                 v-model="ancForm.dangerSigns" id="fever">
                          <label class="form-check-label" for="fever">
                            Fever
                          </label>
                        </div>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" value="swelling"
                                 v-model="ancForm.dangerSigns" id="swelling">
                          <label class="form-check-label" for="swelling">
                            Excessive swelling
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Complaints -->
                  <div class="mb-3">
                    <label class="form-label">Current Complaints</label>
                    <textarea v-model="ancForm.complaints" class="form-control" rows="2"
                              placeholder="Patient's current complaints or symptoms..."></textarea>
                  </div>

                  <!-- Clinical Notes -->
                  <div class="mb-3">
                    <label class="form-label">Clinical Notes</label>
                    <textarea v-model="ancForm.clinicalNotes" class="form-control" rows="3"
                              placeholder="Clinical observations and examination findings..."></textarea>
                  </div>
                </div>
              </div>

              <!-- Services Provided -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Services Provided</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" v-model="ancForm.ttVaccinationGiven"
                               id="ttVaccination">
                        <label class="form-check-label" for="ttVaccination">
                          TT Vaccination Given
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" v-model="ancForm.ironSupplementation"
                               id="ironSupp">
                        <label class="form-check-label" for="ironSupp">
                          Iron/Folic Acid Supplementation
                        </label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" v-model="ancForm.dewormingGiven"
                               id="deworming">
                        <label class="form-check-label" for="deworming">
                          Deworming Medication
                        </label>
                      </div>
                      <div class="form-check mb-2">
                        <input class="form-check-input" type="checkbox" v-model="ancForm.counselingProvided"
                               id="counseling">
                        <label class="form-check-label" for="counseling">
                          Birth Preparedness Counseling
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <!-- Step 3: Referral & Next Visit -->
          <div v-else-if="step === 3">
            <div class="mb-4">
              <h6 class="mb-3">Referral & Follow-up</h6>

              <!-- High Risk Alert -->
              <div v-if="isHighRisk" class="alert alert-danger mb-4">
                <h6 class="alert-heading">
                  <i class="bx bx-error-circle"></i> High Risk Indicators Detected
                </h6>
                <ul class="mb-0">
                  <li v-if="ancForm.bloodPressureSystolic >= 140 || ancForm.bloodPressureDiastolic >= 90">
                    High blood pressure ({{ ancForm.bloodPressureSystolic }}/{{ ancForm.bloodPressureDiastolic }})
                  </li>
                  <li v-for="sign in ancForm.dangerSigns" :key="sign">
                    {{ formatDangerSign(sign) }}
                  </li>
                </ul>
                <hr>
                <strong>Immediate referral recommended</strong>
              </div>

              <!-- Referral Decision -->
              <div class="card mb-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Referral Decision</h6>
                </div>
                <div class="card-body">
                  <div class="form-check mb-3">
                    <input class="form-check-input" type="checkbox" v-model="ancForm.referralNeeded"
                           id="referralCheck">
                    <label class="form-check-label" for="referralCheck">
                      Referral needed
                    </label>
                  </div>

                  <div v-if="ancForm.referralNeeded" class="mt-3">
                    <div class="mb-3">
                      <label class="form-label">Referral Reason <span class="text-danger">*</span></label>
                      <textarea v-model="ancForm.referralReason" class="form-control" rows="2"
                                placeholder="Specify reason for referral..." required></textarea>
                    </div>

                    <div class="mb-3">
                      <label class="form-label">Refer To</label>
                      <select v-model="ancForm.referralFacility" class="form-select">
                        <option value="health_center">Health Center</option>
                        <option value="hospital">Hospital</option>
                        <option value="specialized_clinic">Specialized Clinic</option>
                      </select>
                    </div>

                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" v-model="ancForm.urgentReferral"
                             id="urgentRef">
                      <label class="form-check-label text-danger" for="urgentRef">
                        <strong>Urgent/Emergency Referral</strong>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Next Visit -->
              <div class="card">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Schedule Next Visit</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Next Visit Date</label>
                      <input v-model="ancForm.nextVisitDate" type="date" class="form-control"
                             :min="tomorrow">
                    </div>
                    <div class="col-md-6 mb-3">
                      <label class="form-label">Visit Purpose</label>
                      <select v-model="ancForm.nextVisitPurpose" class="form-select">
                        <option value="routine_anc">Routine ANC</option>
                        <option value="follow_up">Follow-up</option>
                        <option value="lab_results">Lab Results Review</option>
                        <option value="delivery_planning">Delivery Planning</option>
                      </select>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Notes for Next Visit</label>
                    <textarea v-model="ancForm.nextVisitNotes" class="form-control" rows="2"
                              placeholder="Special instructions or focus areas for next visit..."></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="moveToStep2" :disabled="!selectedPregnancy">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <button v-else-if="step === 2" type="button" class="btn btn-primary"
                  @click="nextStep">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 3">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="recordANCVisit"
                    :disabled="submitting || (ancForm.referralNeeded && !ancForm.referralReason)">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Recording...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Complete Visit
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ANCVisitModal",
  props: {
    preselectedPregnancy: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Pregnant women data
      searchQuery: '',
      pregnantWomen: {
        overdue: [],
        due_this_week: [],
        due_this_month: [],
        high_risk: [],
        regular_followup: []
      },
      selectedPregnancy: null,

      // ANC form
      ancForm: {
        visitNumber: 1,
        weight: null,
        height: null,
        bloodPressureSystolic: null,
        bloodPressureDiastolic: null,
        temperature: null,
        fundalHeight: null,
        fetalHeartRate: null,
        fetalPosition: '',
        dangerSigns: [],
        complaints: '',
        clinicalNotes: '',
        ttVaccinationGiven: false,
        ironSupplementation: false,
        dewormingGiven: false,
        counselingProvided: false,
        referralNeeded: false,
        referralReason: '',
        referralFacility: 'health_center',
        urgentReferral: false,
        nextVisitDate: '',
        nextVisitPurpose: 'routine_anc',
        nextVisitNotes: ''
      },

      tomorrow: new Date(Date.now() + 86400000).toISOString().split('T')[0]
    };
  },

  computed: {
    noPregnantWomen() {
      return Object.values(this.pregnantWomen).every(arr => arr.length === 0);
    },

    isHighRisk() {
      // Check for high risk indicators
      if (this.ancForm.bloodPressureSystolic >= 140 || this.ancForm.bloodPressureDiastolic >= 90) {
        return true;
      }
      if (this.ancForm.dangerSigns.length > 0) {
        return true;
      }
      if (this.selectedPregnancy?.high_risk) {
        return true;
      }
      return false;
    }
  },

  mounted() {
    if (this.preselectedPregnancy) {
      this.selectedPregnancy = this.preselectedPregnancy;
      this.step = 2;
    } else {
      this.loadPregnantWomen();
    }
  },

  methods: {
    async loadPregnantWomen() {
      this.loading = true;

      const data = {
        "function": "getPregnantWomen"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.pregnantWomen = res.data.data.categories || {
            overdue: [],
            due_this_week: [],
            due_this_month: [],
            high_risk: [],
            regular_followup: []
          };
        }
      } catch (e) {
        console.error("Failed to load pregnant women:", e);
      } finally {
        this.loading = false;
      }
    },

    async searchPregnantWomen() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        await this.loadPregnantWomen();
        return;
      }

      this.loading = true;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery,
        "searchType": "pregnant"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Reset categories and put all results in regular_followup
          this.pregnantWomen = {
            overdue: [],
            due_this_week: [],
            due_this_month: [],
            high_risk: [],
            regular_followup: res.data.data.members || []
          };
        }
      } catch (e) {
        console.error("Failed to search pregnant women:", e);
      } finally {
        this.loading = false;
      }
    },

    selectPregnancy(pregnancy) {
      this.selectedPregnancy = pregnancy;
    },

    moveToStep2() {
      if (!this.selectedPregnancy) return;

      // Set visit number
      this.ancForm.visitNumber = (this.selectedPregnancy.anc_visit_count || 0) + 1;

      // Auto-set next visit date based on gestational age
      this.setDefaultNextVisitDate();

      this.step = 2;
    },

    nextStep() {
      if (this.step === 2) {
        // Check for high risk indicators
        if (this.isHighRisk) {
          this.ancForm.referralNeeded = true;
        }
        this.step = 3;
      }
    },

    previousStep() {
      if (this.step === 3) {
        this.step = 2;
      } else if (this.step === 2) {
        this.step = 1;
      }
    },

    setDefaultNextVisitDate() {
      const weeks = this.selectedPregnancy.current_gestational_weeks || 0;
      let daysToAdd = 28; // Default 4 weeks

      if (weeks < 28) {
        daysToAdd = 28; // Monthly
      } else if (weeks < 36) {
        daysToAdd = 14; // Bi-weekly
      } else {
        daysToAdd = 7; // Weekly
      }

      const nextDate = new Date();
      nextDate.setDate(nextDate.getDate() + daysToAdd);
      this.ancForm.nextVisitDate = nextDate.toISOString().split('T')[0];
    },

    async recordANCVisit() {
      if (this.ancForm.referralNeeded && !this.ancForm.referralReason) {
        alert('Please provide referral reason');
        return;
      }

      this.submitting = true;

      const data = {
        "function": "recordANCVisit",
        "pregnancyId": this.selectedPregnancy.pregnancy_id,
        "visitNumber": this.ancForm.visitNumber,
        "weight": this.ancForm.weight,
        "height": this.ancForm.height,
        "bloodPressureSystolic": this.ancForm.bloodPressureSystolic,
        "bloodPressureDiastolic": this.ancForm.bloodPressureDiastolic,
        "temperature": this.ancForm.temperature,
        "fundalHeight": this.ancForm.fundalHeight,
        "fetalHeartRate": this.ancForm.fetalHeartRate,
        "dangerSigns": this.ancForm.dangerSigns.join(', '),
        "complaints": this.ancForm.complaints,
        "clinicalNotes": this.ancForm.clinicalNotes,
        "ttVaccinationGiven": this.ancForm.ttVaccinationGiven,
        "ironSupplementation": this.ancForm.ironSupplementation,
        "referralNeeded": this.ancForm.referralNeeded,
        "referralReason": this.ancForm.referralReason,
        "nextVisitDate": this.ancForm.nextVisitDate
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // If referral needed, create referral
          if (this.ancForm.referralNeeded) {
            await this.createReferral();
          }

          this.$emit('recorded', {
            ancId: res.data.data.ancId,
            highRiskDetected: res.data.data.highRiskDetected
          });
        } else {
          alert(res.data.message || 'Failed to record ANC visit');
        }
      } catch (e) {
        console.error("Failed to record ANC visit:", e);
        alert('Failed to record ANC visit. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    async createReferral() {
      const referralData = {
        "function": "createReferral",
        "memberId": this.selectedPregnancy.member_id,
        "referralType": "anc_complication",
        "referralReason": this.ancForm.referralReason,
        "referralTo": this.ancForm.referralFacility,
        "urgency": this.ancForm.urgentReferral ? 'urgent' : 'routine',
        "clinicalNotes": `ANC Visit #${this.ancForm.visitNumber} - ${this.ancForm.clinicalNotes}`
      };

      try {
        await executeJson(referralData, authUrl);
      } catch (e) {
        console.error("Failed to create referral:", e);
      }
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        return age - 1;
      }
      return age;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatDangerSign(sign) {
      const signs = {
        'vaginal_bleeding': 'Vaginal bleeding',
        'severe_headache': 'Severe headache',
        'blurred_vision': 'Blurred vision',
        'convulsions': 'Convulsions',
        'severe_abdominal_pain': 'Severe abdominal pain',
        'reduced_fetal_movement': 'Reduced fetal movement',
        'fever': 'Fever',
        'swelling': 'Excessive swelling'
      };
      return signs[sign] || sign;
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.list-group-item.border-danger {
  border-left: 3px solid #dc3545;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.card-header {
  font-weight: 600;
}

.form-check-input {
  cursor: pointer;
}
</style>